import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Radio, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mason`;
const SCRIPT = 'https://unpkg.com/@elevenlabs/convai-widget-embed';

const loadScript = () => new Promise((res, rej) => {
  if (window.customElements?.get('elevenlabs-convai') || document.querySelector(`script[src="${SCRIPT}"]`)) return res();
  const s = document.createElement('script'); s.src = SCRIPT; s.async = true; s.onload = res; s.onerror = rej; document.head.appendChild(s);
});

// "Talk to MASON": fetches a 15-minute signed URL from Ops (the ElevenLabs key stays server-side) and mounts the voice widget.
export const MasonWidget = () => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const holder = useRef(null);
  useEffect(() => { axios.get(`${API}/widget/config`).then(r => setEnabled(!!r.data.enabled)).catch(() => {}); }, []);
  const start = async () => {
    setLoading(true);
    try {
      const [{ data }] = await Promise.all([axios.get(`${API}/widget/signed-url`), loadScript()]);
      if (!data.signed_url) throw new Error('no url');
      setOpen(true);
      requestAnimationFrame(() => {
        if (!holder.current) return;
        holder.current.innerHTML = '';
        const el = document.createElement('elevenlabs-convai');
        el.setAttribute('signed-url', data.signed_url);
        el.setAttribute('agent-id', data.agent_id);
        el.setAttribute('dynamic-variables', JSON.stringify(data.dynamic_variables || { caller_name: data.user_name || '' }));
        holder.current.appendChild(el);
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'MASON is not reachable right now'); }
    finally { setLoading(false); }
  };
  const stop = () => { if (holder.current) holder.current.innerHTML = ''; setOpen(false); };
  if (!enabled) return null;
  return (
    <>
      {!open && (
        <button type="button" onClick={start} disabled={loading} data-testid="mason-talk-btn"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-3 pr-4 py-2.5 shadow-xl hover:bg-slate-800 disabled:opacity-60 transition-colors">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" /></span>
          <Radio className="w-4 h-4" /> <span className="text-sm font-bold">{loading ? 'Connecting…' : 'Talk to MASON'}</span>
        </button>
      )}
      {open && (
        <button type="button" onClick={stop} data-testid="mason-close-btn" title="Hide MASON"
          className="fixed bottom-24 right-5 z-40 h-8 px-2.5 rounded-full bg-white border border-slate-300 text-slate-600 text-xs font-semibold shadow flex items-center gap-1"><X className="w-3.5 h-3.5" /> Hide MASON</button>
      )}
      <div ref={holder} data-testid="mason-widget-holder" />
    </>
  );
};
